import React, { useState } from 'react';
import { Button } from './ui/button';
import { FileText, Download, Sparkles, Trash2, Eye, Loader2, CheckCircle, AlertTriangle, Wand2, Table, Target } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import DocumentUpload from './DocumentUpload';
import DocumentViewer from './DocumentViewer';
import DocumentVisibilityControl from './DocumentVisibilityControl';
import GridReview from './GridReview';
import DraftingModule from './DraftingModule';
import SimulationModule from './SimulationModule';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DocumentManager = ({ caseId, caseData, documents = [], onDocumentsChanged, onUpdate }: { caseId: string; caseData?: any; documents?: any[]; onDocumentsChanged?: () => void; onUpdate?: () => void }) => {
  const [analyzing, setAnalyzing] = useState({});
  const [viewDocument, setViewDocument] = useState(null);
  const [showGridReview, setShowGridReview] = useState(false);
  const [showDrafting, setShowDrafting] = useState(false);
  const [showSimulation, setShowSimulation] = useState(false);
  const [documentStatuses, setDocumentStatuses] = useState({});
  const [expandedDoc, setExpandedDoc] = useState(null);

  // Debug log
  console.log('DocumentManager rendered', { caseId, documentsCount: documents?.length });

  const analyzeDocument = async (documentId) => {
    setAnalyzing({ ...analyzing, [documentId]: true });
    try {
      const response = await axios.post(`${API}/documents/${documentId}/analyze`);
      toast.success('Dokument analysert!');
      if (onDocumentsChanged) onDocumentsChanged();
    } catch (error) {
      console.error('Analysis error:', error);
      toast.error('Kunne ikke analysere dokument');
    } finally {
      setAnalyzing({ ...analyzing, [documentId]: false });
    }
  };

  const deleteDocument = async (documentId) => {
    if (!window.confirm('Er du sikker på at du vil slette dette dokumentet?')) return;
    
    try {
      await axios.delete(`${API}/documents/${documentId}`);
      toast.success('Dokument slettet');
      if (onDocumentsChanged) onDocumentsChanged();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Kunne ikke slette dokument');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Dokumenter</h2>
          <p className="text-sm text-gray-600">{documents.length} {documents.length === 1 ? 'dokument' : 'dokumenter'} totalt</p>
        </div>
        
        {documents.length > 0 && (
          <Button
            onClick={() => setShowGridReview(true)}
            size="sm"
            className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white rounded-xl shadow-sm"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            AI Grid Review
          </Button>
        )}
      </div>

      {/* Upload Section */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-gray-600" />
          Last opp dokumenter
        </h3>
        <DocumentUpload 
          caseId={caseId} 
          onUploaded={() => {
            if (onDocumentsChanged) onDocumentsChanged();
          }}
        />
      </div>

      {/* Document List */}
      <div>
        {documents.length === 0 ? (
          <div className="text-center py-16 bg-gray-50 rounded-xl border border-gray-200 shadow-sm">
            <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-base text-gray-700 mb-2 font-semibold">Ingen dokumenter lastet opp ennå</p>
            <p className="text-sm text-gray-600">Last opp dine første dokumenter for å komme i gang</p>
          </div>
        ) : (
          <div className="space-y-4">
            {documents.map((doc) => {
              const hasAnalysis = doc.ai_metadata;
              const riskScore = doc.ai_metadata?.risk_score;
              
              return (
                <div key={doc.id} className="bg-white border border-gray-200 rounded-xl p-6 hover:border-gray-300 hover:shadow-md transition-all group shadow-sm">
                  <div className="flex items-start gap-4">
                    {/* File Icon */}
                    <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-gray-200 transition-colors">
                      <FileText className="w-6 h-6 text-gray-600" />
                    </div>
                    
                    {/* File Info */}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 mb-2 truncate">
                        {doc.filename}
                      </div>
                      <div className="flex items-center gap-2 mb-3 flex-wrap">
                        <span className="text-xs px-2.5 py-1 bg-gray-100 text-gray-700 rounded-full font-medium">
                          {doc.file_type}
                        </span>
                        <span className="text-xs text-gray-500 font-medium">
                          {(doc.file_size / 1024).toFixed(1)} KB
                        </span>
                        <DocumentVisibilityControl
                          documentId={doc.id}
                          currentVisibility={doc.visibility || 'PRIVATE_ME'}
                          onVisibilityChanged={() => {
                            if (onDocumentsChanged) onDocumentsChanged();
                          }}
                        />
                      </div>
                      
                      {/* AI Analysis Status */}
                      {hasAnalysis ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs px-3 py-1 bg-violet-50 text-violet-700 border border-violet-200 rounded-full font-semibold flex items-center gap-1.5">
                            <CheckCircle className="w-3.5 h-3.5" />
                            AI-analysert
                          </span>
                          {riskScore && (
                            <span className={`text-xs px-3 py-1 rounded-full font-semibold border ${
                              riskScore <= 3 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                              riskScore <= 6 ? 'bg-amber-50 text-amber-700 border-amber-200' :
                              'bg-red-50 text-red-700 border-red-200'
                            }`}>
                              Risiko: {riskScore}/10
                            </span>
                          )}
                          {doc.ai_metadata?.dates?.length > 0 && (
                            <span className="text-xs px-3 py-1 bg-gray-100 text-gray-700 border border-gray-200 rounded-full font-semibold">
                              {doc.ai_metadata.dates.length} frister
                            </span>
                          )}
                        </div>
                      ) : (
                        <button
                          onClick={() => analyzeDocument(doc.id)}
                          disabled={analyzing[doc.id]}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white text-xs font-semibold rounded-lg shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {analyzing[doc.id] ? (
                            <>
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              AI analyserer...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-3.5 h-3.5" />
                              Analyser med AI
                            </>
                          )}
                        </button>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => setViewDocument(doc)}
                        className="p-2.5 hover:bg-gray-100 rounded-xl transition-colors"
                        title="Se dokument"
                      >
                        <Eye className="w-4 h-4 text-gray-600" />
                      </button>
                      <button
                        onClick={() => deleteDocument(doc.id)}
                        className="p-2.5 hover:bg-red-50 rounded-xl transition-colors"
                        title="Slett"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </div>
                  
                  {/* AI Analysis Results */}
                  {hasAnalysis && doc.ai_metadata && (
                    <div className="mt-5 pt-5 border-t border-gray-200">
                      <div className="text-xs font-semibold text-violet-700 mb-3 uppercase tracking-wide flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5" />
                        AI-analyse
                      </div>
                      {doc.ai_metadata.summary && (
                        <p className="text-sm text-gray-700 mb-4 leading-relaxed bg-violet-50/50 p-3 rounded-xl border border-violet-100">{doc.ai_metadata.summary}</p>
                      )}
                      
                      {/* Risks */}
                      {doc.ai_metadata.risks && doc.ai_metadata.risks.length > 0 && (
                        <div className="space-y-2 mb-4">
                          {doc.ai_metadata.risks.slice(0, 2).map((risk, i) => (
                            <div key={i} className={`flex items-start gap-2.5 text-sm p-3 rounded-xl ${
                              risk.severity === 'HIGH' ? 'bg-red-50' : 'bg-amber-50'
                            }`}>
                              <AlertTriangle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                                risk.severity === 'HIGH' ? 'text-red-600' : 'text-amber-600'
                              }`} />
                              <span className="text-gray-700 font-medium">{risk.description}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Key info */}
                      {doc.ai_metadata.parties && doc.ai_metadata.parties.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {doc.ai_metadata.parties.slice(0, 3).map((party, i) => (
                            <span key={i} className="text-xs px-3 py-1.5 bg-gray-100 text-gray-700 border border-gray-200 rounded-full font-medium">
                              {party}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Lexy Pro AI Features - Dark section with purple accent */}
      {documents.length > 0 && (
        <div className="relative overflow-hidden bg-gray-900 rounded-xl p-8 shadow-lg">
          {/* Decorative blur */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-violet-500 to-purple-600 rounded-full blur-3xl opacity-20"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-br from-violet-600 to-gray-800 rounded-full blur-3xl opacity-15"></div>
          </div>

          <div className="relative z-10">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/20">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Lexy Pro AI</h3>
                  <p className="text-sm text-gray-400">Kraftige AI-verktøy for dokumenter</p>
                </div>
              </div>
              <span className="px-3 py-1 bg-violet-500/20 text-violet-300 text-xs font-semibold rounded-full border border-violet-500/30">
                AI-DREVET
              </span>
            </div>
            
            {/* AI Tools Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* AI Draft */}
              <button
                onClick={() => setShowDrafting(true)}
                className="relative overflow-hidden p-6 bg-gray-800/80 backdrop-blur-sm border border-gray-700 rounded-xl hover:border-violet-500/50 hover:bg-gray-800 transition-all duration-300 group text-left"
              >
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-violet-500/10 to-transparent rounded-bl-3xl opacity-50 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative z-10">
                  <div className="w-14 h-14 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center mb-4 shadow-lg shadow-violet-500/20 group-hover:scale-105 transition-transform">
                    <Wand2 className="w-7 h-7 text-white" />
                  </div>
                  <h4 className="text-base font-bold text-white mb-1.5">AI Draft</h4>
                  <p className="text-sm text-gray-400 leading-relaxed">Generer profesjonelle juridiske dokumenter automatisk</p>
                </div>
              </button>
              
              {/* Grid Review */}
              <button
                onClick={() => setShowGridReview(true)}
                className="relative overflow-hidden p-6 bg-gray-800/80 backdrop-blur-sm border border-gray-700 rounded-xl hover:border-violet-500/50 hover:bg-gray-800 transition-all duration-300 group text-left"
              >
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-violet-500/10 to-transparent rounded-bl-3xl opacity-50 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative z-10">
                  <div className="w-14 h-14 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center mb-4 shadow-lg shadow-violet-500/20 group-hover:scale-105 transition-transform">
                    <Table className="w-7 h-7 text-white" />
                  </div>
                  <h4 className="text-base font-bold text-white mb-1.5">Grid Review</h4>
                  <p className="text-sm text-gray-400 leading-relaxed">Sammenlign og analyser flere dokumenter samtidig</p>
                </div>
              </button>
              
              {/* Simulation */}
              <button
                onClick={() => setShowSimulation(true)}
                className="relative overflow-hidden p-6 bg-gray-800/80 backdrop-blur-sm border border-gray-700 rounded-xl hover:border-violet-500/50 hover:bg-gray-800 transition-all duration-300 group text-left"
              >
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-violet-500/10 to-transparent rounded-bl-3xl opacity-50 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative z-10">
                  <div className="w-14 h-14 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center mb-4 shadow-lg shadow-violet-500/20 group-hover:scale-105 transition-transform">
                    <Target className="w-7 h-7 text-white" />
                  </div>
                  <h4 className="text-base font-bold text-white mb-1.5">Simulation</h4>
                  <p className="text-sm text-gray-400 leading-relaxed">Test juridiske argumenter og strategier</p>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Document Viewer Modal */}
      {viewDocument && (
        <DocumentViewer
          document={viewDocument}
          onClose={() => setViewDocument(null)}
          onAnalyze={(docId) => {
            analyzeDocument(docId);
            setViewDocument(null);
          }}
        />
      )}
      
      {/* Grid Review Fullscreen */}
      {showGridReview && (
        <GridReview
          caseId={caseId}
          documents={documents}
          onClose={() => setShowGridReview(false)}
        />
      )}
      
      {/* Drafting Module Fullscreen */}
      {showDrafting && (
        <>
          <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-50" onClick={() => setShowDrafting(false)} />
          <div className="fixed inset-8 z-50 bg-white rounded-2xl shadow-2xl">
            <DraftingModule caseId={caseId} caseData={caseData} onClose={() => setShowDrafting(false)} />
          </div>
        </>
      )}
      
      {/* Simulation Module Fullscreen */}
      {showSimulation && (
        <>
          <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-50" onClick={() => setShowSimulation(false)} />
          <div className="fixed inset-8 z-50 bg-white rounded-2xl shadow-2xl">
            <SimulationModule caseId={caseId} onClose={() => setShowSimulation(false)} />
          </div>
        </>
      )}
    </div>
  );
};

export default DocumentManager;
